// ── Date-format detector (pure) ──────────────────────────────────────────
// Looks at a column's held values (<= 200 rows, blanks ignored) and says
// which toDate() format reading them needs. It never decides for the user:
// the editor only inserts a format after an explicit click.
//
//   kind                  meaning                                     suggestedFormat
//   iso_or_year_first     YYYY-MM-DD, YYYY/MM/DD, datetimes, 8 digits  null (toDate(x) needs no format)
//   day_first             a first part > 12 was seen (DD/MM/YYYY)      "DD<sep>MM<sep>YYYY"
//   month_first           a second part > 12 was seen (MM/DD/YYYY)    "MM<sep>DD<sep>YYYY"
//   mixed                 both orders seen / mixed styles/separators   null
//   ambiguous             d/m/y shaped, every part <= 12               null (ask the user)
//   not_dates             fewer than 80% of the non-blank values look   null
//                         like a date
//
// The observed separator is kept in the format. When a day or month part is
// written without a leading zero somewhere, the 1-2 digit tokens (D, M) are
// suggested instead of DD / MM, so every value parses.

export const MAX_ROWS = 200;
export const THRESHOLD_PERCENT = 80;

const RE_ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ]\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const RE_YEAR_FIRST = /^(\d{4})([/.-])(\d{1,2})\2(\d{1,2})$/;
const RE_COMPACT = /^(\d{4})(\d{2})(\d{2})$/;
const RE_A_FIRST = /^(\d{1,2})([/.-])(\d{1,2})\2(\d{4})(?:[T ]\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/;

function isBlankValue(v) {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  return s === '' || s === 'N/A' || s === '—';
}

// What one held value looks like: {style:'iso'|'year_first'|'compact'|'a_first', ...} or null.
export function classify(v) {
  if (typeof v !== 'string') return null;            // numbers (serials), booleans, ...: not date text
  const s = v.trim();
  let m = RE_ISO.exec(s);
  if (m && +m[2] >= 1 && +m[2] <= 12 && +m[3] >= 1 && +m[3] <= 31) return { style: 'iso' };
  m = RE_YEAR_FIRST.exec(s);
  if (m && +m[3] >= 1 && +m[3] <= 12 && +m[4] >= 1 && +m[4] <= 31) return { style: 'year_first', sep: m[2] };
  m = RE_COMPACT.exec(s);
  if (m && +m[1] >= 1000 && +m[2] >= 1 && +m[2] <= 12 && +m[3] >= 1 && +m[3] <= 31) return { style: 'compact' };
  m = RE_A_FIRST.exec(s);
  if (m && +m[1] >= 1 && +m[1] <= 31 && +m[3] >= 1 && +m[3] <= 31) {
    return { style: 'a_first', sep: m[2], a: +m[1], b: +m[3], padded: m[1].length === 2 && m[3].length === 2 };
  }
  return null;
}

export function detectDateFormat(values) {
  const held = (Array.isArray(values) ? values : []).slice(0, MAX_ROWS).filter(v => !isBlankValue(v));
  const evidence = {
    nonBlank: held.length, dateLike: 0, yearFirst: 0, aFirst: 0, dayFirst: 0, monthFirst: 0,
    separators: [], allPadded: true, examples: [],
  };
  const seps = new Set();
  for (const v of held) {
    const c = classify(v);
    if (!c) continue;
    evidence.dateLike += 1;
    if (evidence.examples.length < 3) evidence.examples.push(String(v).trim());
    if (c.style === 'a_first') {
      evidence.aFirst += 1;
      seps.add(c.sep);
      if (c.a > 12) evidence.dayFirst += 1;
      if (c.b > 12) evidence.monthFirst += 1;
      if (!c.padded) evidence.allPadded = false;
    } else {
      evidence.yearFirst += 1;
    }
  }
  evidence.separators = [...seps];

  const none = (kind) => ({ kind, suggestedFormat: null, evidence });
  if (evidence.nonBlank === 0 || evidence.dateLike * 100 < evidence.nonBlank * THRESHOLD_PERCENT) return none('not_dates');
  if (evidence.aFirst === 0) return none('iso_or_year_first');
  if (evidence.yearFirst > 0) return none('mixed');
  if (evidence.dayFirst > 0 && evidence.monthFirst > 0) return none('mixed');
  if (evidence.separators.length > 1) return none('mixed');
  const sep = evidence.separators[0];
  const d = evidence.allPadded ? 'DD' : 'D';
  const m = evidence.allPadded ? 'MM' : 'M';
  if (evidence.dayFirst > 0) return { kind: 'day_first', suggestedFormat: `${d}${sep}${m}${sep}YYYY`, evidence };
  if (evidence.monthFirst > 0) return { kind: 'month_first', suggestedFormat: `${m}${sep}${d}${sep}YYYY`, evidence };
  return none('ambiguous');
}

// The held values of one column from the editor's rows.
export function columnValues(rows, name, cap = MAX_ROWS) {
  const out = [];
  for (const r of rows || []) {
    if (out.length >= cap) break;
    out.push(r?.[name]);
  }
  return out;
}

// Whether a column gets the "date?" hint.
export function looksLikeDates(result) {
  return !!result && result.kind !== 'not_dates';
}

const SEP_FOR_CHOICE = '/';

// What the user can pick after clicking the hint: a list of
// {id, label, format|null}. A format is only ever inserted from one of these
// by an explicit click. "ambiguous" and "mixed" offer both orders and never
// pre-select one.
export function insertOptions(result) {
  if (!result || result.kind === 'not_dates') return [];
  if (result.kind === 'iso_or_year_first') return [{ id: 'no-format', label: 'Use as is (no format needed)', format: null }];
  if (result.kind === 'day_first' || result.kind === 'month_first') {
    return [{ id: 'suggested', label: `Parse as ${result.suggestedFormat}`, format: result.suggestedFormat }];
  }
  const seps = result.evidence?.separators || [];
  const sep = seps.length === 1 ? seps[0] : SEP_FOR_CHOICE;
  return [
    { id: 'dmy', label: `DD${sep}MM${sep}YYYY (day first)`, format: `DD${sep}MM${sep}YYYY` },
    { id: 'mdy', label: `MM${sep}DD${sep}YYYY (month first)`, format: `MM${sep}DD${sep}YYYY` },
  ];
}

// Plain-language line for the hint panel.
export function describeResult(result) {
  if (!result) return '';
  const ex = result.evidence?.examples?.length ? ` (e.g. ${result.evidence.examples.join(', ')})` : '';
  switch (result.kind) {
    case 'iso_or_year_first': return `Looks like year-first dates${ex}; toDate() reads these without a format.`;
    case 'day_first': return `Looks like day-first dates${ex}: a day greater than 12 appears in the first position.`;
    case 'month_first': return `Looks like month-first dates${ex}: a day greater than 12 appears in the second position.`;
    case 'mixed': return `The values mix date styles or orders${ex}. Pick the format to use; rows that don't match become blank.`;
    case 'ambiguous': return `Every value could be day-first or month-first${ex}. Which one is it?`;
    default: return 'These values don’t look like dates.';
  }
}
